/**
 * Moo LLM Service - AI Assistant Integration
 *
 * Integrates with LLM providers (OpenAI, Claude, etc.) to generate
 * contextual responses for the Moo AI assistant.
 */
/**
 * Generate a response from the Moo AI assistant
 * Uses OpenAI API if key is available, otherwise provides a smart fallback response
 */
export async function generateMooResponse(userMessage, context) {
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY ||
        process.env.NEXT_PUBLIC_OPENAI_API_KEY;
    // If no API key, use intelligent fallback
    if (!apiKey) {
        return generateFallbackResponse(userMessage, context);
    }
    try {
        const response = await callOpenAIAPI(userMessage, context, apiKey);
        return response;
    }
    catch (error) {
        console.error('[Moo] Failed to call OpenAI API:', error);
        // Fallback to mock response on API error
        return generateFallbackResponse(userMessage, context);
    }
}
/**
 * Call OpenAI Chat Completion API
 */
async function callOpenAIAPI(userMessage, context, apiKey) {
    const systemPrompt = buildSystemPrompt(context);
    const requestBody = {
        model: 'gpt-3.5-turbo',
        messages: [
            {
                role: 'system',
                content: systemPrompt,
            },
            {
                role: 'user',
                content: userMessage,
            },
        ],
        temperature: 0.7,
        max_tokens: 300,
        top_p: 0.9,
    };
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(`OpenAI API error: ${error.error?.message || 'Unknown error'}`);
    }
    const data = await response.json();
    const content = data.choices[0]?.message?.content;
    if (!content) {
        throw new Error('No response from OpenAI API');
    }
    return {
        content,
        tokens: data.usage?.total_tokens,
        model: data.model,
    };
}
/**
 * Build system prompt with board context
 */
function buildSystemPrompt(context) {
    const recentActivityStr = context.recentActivity?.length
        ? `\nRecent activity: ${context.recentActivity.join(', ')}`
        : '';
    const descriptionStr = context.boardDescription
        ? `\nBoard description: ${context.boardDescription}`
        : '';
    return `You are Moo, a helpful and friendly AI assistant for task management and productivity. You're represented by a cow emoji 🐄 and have a warm, encouraging personality.

You're helping a user organize their work on a board called "${context.boardTitle}".${descriptionStr}${recentActivityStr}

Your role is to:
- Help organize and prioritize tasks
- Provide encouragement and motivation
- Suggest improvements and best practices
- Answer questions about productivity and task management
- Keep responses concise and actionable (max 2-3 sentences)
- Use a friendly, professional tone
- Occasionally include relevant emojis to match your cow personality

Always be helpful and constructive. Focus on moving the user's work forward.`;
}
/**
 * Generate a smart fallback response without API
 * Creates contextual responses based on message patterns
 */
function generateFallbackResponse(userMessage, context) {
    const message = userMessage.toLowerCase();
    const responses = [];
    // Pattern matching for common requests
    if (message.includes('help') || message.includes('?')) {
        responses.push(`I'm here to help you with your "${context.boardTitle}" board! Try asking me about prioritizing tasks, organizing your work, or managing your time. 🐄`);
    }
    else if (message.includes('priority') || message.includes('important')) {
        responses.push(`Great question! For the "${context.boardTitle}" board, I'd suggest focusing on the most impactful tasks first. What's your biggest challenge right now? 🐄`);
    }
    else if (message.includes('thanks') || message.includes('great') || message.includes('good')) {
        responses.push(`Happy to help with your "${context.boardTitle}" board! Keep up the great work! 🐄`);
    }
    else if (message.includes('stuck') || message.includes('issue') || message.includes('problem')) {
        responses.push(`I'm sorry you're stuck! Let's break down the problem together. What specific challenge are you facing with your "${context.boardTitle}" board? 🐄`);
    }
    else {
        // Generic contextual response
        responses.push(`I'm thinking about "${userMessage}" in the context of your "${context.boardTitle}" board. How can I help you move forward? 🐄`);
    }
    return {
        content: responses[0],
        model: 'fallback',
    };
}
/**
 * Generate system context from board activity
 * This would fetch actual board data in a real implementation
 */
export async function buildMooContext(boardId, boardTitle, userName) {
    // In a real implementation, this would fetch from Supabase:
    // - Recent board activity
    // - Board description
    // - Member activity
    // For now, return a basic context
    return {
        boardTitle,
        boardId,
        userName,
        boardDescription: undefined,
        recentActivity: [],
    };
}
